import { Fragment, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Button, TextField } from '@mui/material';
import Grid from '@mui/material/Grid2';
import intl from 'react-intl-universal';

import ResultChip from '../../DiagnosticResult/ResultChip';

import { Title, ValueFill, CloseButton } from './styles';
import { addDiagnosis } from 'views/Kenshin/Assignment/api';

const propTypes = {
  assignments: PropTypes.array,
  diagnosis: PropTypes.any,
  diagnosticNote: PropTypes.any,
  setDiagnosticNote: PropTypes.any.isRequired,
  currentAssignment: PropTypes.any.isRequired,
  updateLocalAssignments: PropTypes.func.isRequired,
  handleGoToNext: PropTypes.func,
  showPrevCase: PropTypes.bool.isRequired,
  closePrevCase: PropTypes.func,
  readOnly: PropTypes.bool.isRequired,
};

const ViewerDiagnosis = ({
  currentAssignment,
  diagnosis,
  diagnosticNote,
  setDiagnosticNote,
  updateLocalAssignments,
  handleGoToNext,
  closePrevCase,
  showPrevCase,
  readOnly,
}) => {
  const handleNoteChange = (event) => {
    setDiagnosticNote(event.target.value);
  };

  const handleClickSick = async () => {
    await addDiagnosis(currentAssignment.id, 'DIAGNOSED_SICK', diagnosticNote);
    updateLocalAssignments(currentAssignment.id, {
      diagnosis: 'DIAGNOSED_SICK',
      diagnosticNote: diagnosticNote,
    });
    if (!showPrevCase) {
      handleGoToNext();
    }
  };

  const handleClickUnknow = async () => {
    await addDiagnosis(
      currentAssignment.id,
      'DIAGNOSED_UNKNOWN',
      diagnosticNote
    );
    updateLocalAssignments(currentAssignment.id, {
      diagnosis: 'DIAGNOSED_UNKNOWN',
      diagnosticNote: diagnosticNote,
    });
    if (!showPrevCase) {
      handleGoToNext();
    }
  };

  const handleClickHealthy = async () => {
    await addDiagnosis(
      currentAssignment.id,
      'DIAGNOSED_HEALTHY',
      diagnosticNote
    );

    updateLocalAssignments(currentAssignment.id, {
      diagnosis: 'DIAGNOSED_HEALTHY',
      diagnosticNote: diagnosticNote,
    });
    if (!showPrevCase) {
      handleGoToNext();
    }
  };

  // Keyboard shortcut
  const handleKeyPress = useCallback(async (e) => {
    const { shiftKey } = e;
    if (shiftKey && e.key === 'Enter') {
      handleClickHealthy();
    }
  }, []);

  useEffect(() => {
    // attach the event listener
    document.addEventListener('keydown', handleKeyPress);

    // remove the event listener
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [handleKeyPress]);

  return (
    <Fragment>
      <Grid container sx={{ margin: 2 }} spacing={1}>
        <Grid size={12}>
          {readOnly && (
            <CloseButton style={{ float: 'right' }} onClick={closePrevCase}>
              {intl.get('confirm_not_check_message_4')}
            </CloseButton>
          )}
        </Grid>
        <Grid size={4}>
          <Title>
            {showPrevCase
              ? readOnly
                ? intl.get(
                    'dashboard_received_assignment_kenshin_viewer_diagnosis_text_prev_case'
                  ) +
                  intl.get(
                    'dashboard_received_assignment_kenshin_viewer_diagnosis_text_diagnosis'
                  )
                : intl.get(
                    'dashboard_received_assignment_kenshin_viewer_diagnosis_text_curr_case'
                  ) +
                  intl.get(
                    'dashboard_received_assignment_kenshin_viewer_diagnosis_text_diagnosis'
                  )
              : intl.get(
                  'dashboard_received_assignment_kenshin_viewer_diagnosis_text_diagnosis'
                )}
          </Title>
        </Grid>
        <Grid size={8}>
          <ResultChip
            result={readOnly ? diagnosis : currentAssignment.diagnosis}
          />
        </Grid>

        <Grid size={12}>
          <ValueFill>
            {intl.get(
              'dashboard_received_assignment_kenshin_viewer_diagnosis_text_note'
            )}
          </ValueFill>
        </Grid>
        <Grid size={12}>
          {readOnly && (
            <TextField
              variant="outlined"
              disabled
              fullWidth
              multiline
              value={diagnosticNote}
              onChange={null}
            />
          )}
          {!readOnly && (
            <TextField
              variant="outlined"
              fullWidth
              multiline
              value={diagnosticNote}
              onChange={handleNoteChange}
            />
          )}
        </Grid>
        {!readOnly && (
          <Grid container justifyContent="space-between" sx={{ width: '100%' }}>
            <Grid>
              <Button
                data-testid="diagnosis-button-sick"
                variant="contained"
                style={{
                  backgroundColor: 'red',
                  fontWeight: 'bold',
                }}
                onClick={handleClickSick}>
                {intl.get('kenshin_case_diagnostic_result_diagnosed_sick')}
              </Button>
            </Grid>
            <Grid>
              <Button
                data-testid="diagnosis-button-unknown"
                variant="contained"
                onClick={handleClickUnknow}
                style={{
                  backgroundColor: 'orange',
                  fontWeight: 'bold',
                }}>
                {intl.get('kenshin_case_diagnostic_result_diagnosed_unknown')}
              </Button>
            </Grid>
            <Grid>
              <Button
                data-testid="diagnosis-button-healthy"
                variant="contained"
                style={{
                  backgroundColor: 'green',
                  fontWeight: 'bold',
                  color: 'white',
                }}
                onClick={handleClickHealthy}>
                {intl.get('kenshin_case_diagnostic_result_diagnosed_healthy')}
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
    </Fragment>
  );
};

ViewerDiagnosis.propTypes = propTypes;

export default ViewerDiagnosis;
